import { useState, useEffect, useCallback } from "react";
import { Moon, Sun, Menu, X, Terminal } from "lucide-react";
import { useTheme } from "../App";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Projects", href: "#projects" },
  { label: "Skills", href: "#skills" },
  { label: "Education", href: "#education" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const { dark, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [menuOpen, setMenuOpen] = useState(false);

  /* ── scroll shadow ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── active section via IntersectionObserver ── */
  useEffect(() => {
    const ids = [
      "hero",
      "about",
      "skills",
      "projects",
      "experience",
      "education",
      "certifications",
      "contact",
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  /* ── close drawer on resize to desktop ── */
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const scrollTo = useCallback((e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    document
      .querySelector(href)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const borderBase = dark ? "border-[#1e293b]" : "border-[#e2e8f0]";
  const bgBase = dark ? "bg-[#111111]/80" : "bg-white/80";
  const textMuted = dark ? "text-[#94a3b8]" : "text-[#64748b]";
  const textPrimary = dark ? "text-[#f1f5f9]" : "text-[#0f172a]";

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? `${bgBase} backdrop-blur-md border-b ${borderBase}/60`
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* ── Logo ── */}
          <a
            href="#hero"
            onClick={(e) => scrollTo(e, "#hero")}
            aria-label="Back to top"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#1e293b] bg-[#111111]/80 hover:border-indigo-500/50 transition-colors duration-200 group"
          >
            <Terminal size={13} className="text-indigo-400" />
            <span className="font-mono text-sm font-semibold text-indigo-400">
              kh
            </span>
            <span className="font-mono text-xs text-[#94a3b8] animate-[blink_1s_step-end_infinite]">
              _
            </span>
          </a>

          {/* ── Desktop links ── */}
          <div className="hidden md:flex items-center gap-0.5">
            {NAV_LINKS.map(({ label, href }) => {
              const active = activeSection === href.slice(1);
              return (
                <a
                  key={href}
                  href={href}
                  onClick={(e) => scrollTo(e, href)}
                  aria-current={active ? "page" : undefined}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    active
                      ? "text-indigo-400"
                      : `${textMuted} hover:${textPrimary}`
                  }`}
                >
                  {active && (
                    <span className="absolute inset-0 rounded-lg bg-indigo-500/10 border border-indigo-500/20" />
                  )}
                  <span className="relative">
                    <span className="font-mono text-indigo-500/50 text-xs mr-0.5">
                      ./
                    </span>
                    {label}
                  </span>
                </a>
              );
            })}
          </div>

          {/* ── Right controls ── */}
          <div className="flex items-center gap-2">
            {/* <button
              onClick={toggle}
              aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
              className={`p-2 rounded-lg border ${borderBase} ${bgBase} ${textMuted} hover:text-indigo-400 hover:border-indigo-500/50 transition-all duration-200`}
            >
              {dark ? <Sun size={15} /> : <Moon size={15} />}
            </button> */}
            <button
              onClick={() => setMenuOpen((o) => !o)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className={`md:hidden p-2 rounded-lg border ${borderBase} ${bgBase} ${textMuted} hover:text-indigo-400 hover:border-indigo-500/50 transition-all duration-200`}
            >
              {menuOpen ? <X size={15} /> : <Menu size={15} />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          menuOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div
          className={`px-4 pb-4 pt-2 border-t border-[#1e293b]/60 ${
            dark ? "bg-[#0a0a0a]/95" : "bg-white/95"
          } backdrop-blur-md`}
        >
          {NAV_LINKS.map(({ label, href }) => {
            const active = activeSection === href.slice(1);
            return (
              <a
                key={href}
                href={href}
                onClick={(e) => scrollTo(e, href)}
                className={`flex items-center gap-2 py-3 px-3 my-0.5 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  active
                    ? "text-indigo-400 bg-indigo-500/10"
                    : `${textMuted} hover:${textPrimary} hover:bg-[#111111]/60`
                }`}
              >
                <span className="font-mono text-indigo-500/50 text-xs">./</span>
                {label}
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
